import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { DashboardDataProvider } from 'hooks/DashboardDataContext'
import useDealer from 'hooks/useDealer'
import useSearchParams from 'hooks/useSearchParams'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import CustomerSearch from 'pages/Dashboard/CustomerSearch'
import DraggableDashboard from 'pages/Dashboard/DraggableDashboard'
import HEHMAcceptModal from 'pages/Dashboard/modals/HEHMAcceptModal'
import HEHMFailureModal from 'pages/Dashboard/modals/HEHMFailureModal'
import PaywallAcceptModal from 'pages/Dashboard/modals/PaywallAcceptModal'
import PaywallFailureModal from 'pages/Dashboard/modals/PaywallFailureModal'
import UnknownFailureModal from 'pages/Dashboard/modals/UnknownFailureModal'
import PinnedCustomersDropdown from './PinnedCustomersDropdown'

import { PageHeader, PageTitle } from 'styles/shared'

interface DashboardProps {
  hasPaywallError?: boolean
  hasPaywallAcceptance?: boolean
  hasHEHMError?: boolean
  hasHEHMAcceptance?: boolean
  hasUnknownPaywallError?: boolean
}

const Dashboard = ({
  hasPaywallError = false,
  hasPaywallAcceptance = false,
  hasHEHMError = false,
  hasHEHMAcceptance = false,
  hasUnknownPaywallError = false
}: DashboardProps) => {
  const navigate = useNavigate()
  const { dealer, dealer: { channel }, isDealerLoaded } = useDealer(0)
  const [displayPaywallAcceptModal, setDisplayPaywallAcceptModal] = useState(hasPaywallAcceptance)
  const [displayPaywallFailureModal, setDisplayPaywallFailureModal] = useState(hasPaywallError)
  const [displayHEHMAcceptModal, setDisplayHEHMAcceptModal] = useState(hasHEHMAcceptance)
  const [displayHEHMFailureModal, setDisplayHEHMFailureModal] = useState(hasHEHMError)
  const [displayUnknownPaywallFailureModal, setDisplayUnknownPaywallFailureModal] = useState(hasUnknownPaywallError)
  const error = useSearchParams('error')

  const { enabledFeatures } = useFeaturesContext()

  const onClosePaywallAcceptCallback = () => {
    setDisplayPaywallAcceptModal(false)
    setDisplayHEHMAcceptModal(false)
    navigate('/dashboard', { replace: true })
  }

  const onClosePaywallFailureCallback = () => {
    setDisplayPaywallFailureModal(false)
    setDisplayHEHMFailureModal(false)
    setDisplayUnknownPaywallFailureModal(false)
    navigate('/dashboard', { replace: true })
  }

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle>Dashboard</PageTitle>

        <S.PageSubHeader>
          {enabledFeatures?.includes('hehm') && <PinnedCustomersDropdown />}
          <CustomerSearch />
        </S.PageSubHeader>
      </S.PageHeader>

      <DashboardDataProvider>
        <DraggableDashboard />
      </DashboardDataProvider>

      {displayPaywallAcceptModal && <PaywallAcceptModal closeCallback={onClosePaywallAcceptCallback} />}
      {displayPaywallFailureModal && <PaywallFailureModal dealerId={dealer.id} closeCallback={onClosePaywallFailureCallback} error={error} />}
      {displayHEHMAcceptModal && isDealerLoaded && <HEHMAcceptModal closeCallback={onClosePaywallAcceptCallback} channel={channel} />}
      {displayHEHMFailureModal && isDealerLoaded && <HEHMFailureModal dealerId={dealer.id} closeCallback={onClosePaywallFailureCallback} error={error} />}
      {displayUnknownPaywallFailureModal && <UnknownFailureModal dealerId={dealer.id} closeCallback={onClosePaywallFailureCallback} error={error} />}
    </S.Container>
  )
}

export default Dashboard

const S = {
  Container: styled.div`
    width: 100%;
  `,
  PageHeader: styled(PageHeader)`
    justify-content: space-between;
    flex-direction: row;
  `,
  PageSubHeader: styled.div`
    display: flex;
    justify-content: end;
    gap: 10px;
    flex: 1;
  `
}
